import i18next, { type TFunction } from 'i18next';

/** BCP-47 tag of the active UI language — numbers and dates follow the language, not the OS. */
export function currentLocale(): string {
  return i18next.resolvedLanguage ?? i18next.language;
}

/** Integer with the active language's grouping ("1,503" / "1 503"). */
export function fmtInt(n: number): string {
  return n.toLocaleString(currentLocale());
}

/** Coerce Postgres count()/bigint/numeric values (which may arrive as strings) to a number. */
export function num(v: unknown): number {
  if (v === null || v === undefined || v === '') return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

export function numOrNull(v: unknown): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** Extract a human-readable message from an error whose message may be a JSON body like {"error": "..."} */
export function errText(err: unknown): string {
  if (err instanceof Error) {
    try {
      const parsed = JSON.parse(err.message) as { error?: unknown; message?: unknown };
      const error = typeof parsed.error === 'string' ? parsed.error : undefined;
      const message = typeof parsed.message === 'string' ? parsed.message : undefined;
      // Our API sends `{ error }` alone; Fastify-default bodies put the reason
      // phrase in `error` ("Bad Request") and the actual detail in `message` —
      // show both rather than losing the detail.
      if (error && message) return `${error}: ${message}`;
      if (error) return error;
      if (message) return message;
    } catch {
      // message is not JSON — use it verbatim
    }
    return err.message;
  }
  return String(err);
}

/**
 * Correlation id the API attached to a failed call — tRPC puts it in `data.requestId`,
 * REST in the JSON body. Shown next to error messages so users can quote it.
 */
export function requestIdOf(err: unknown): string | null {
  if (!(err instanceof Error)) return null;
  const data = (err as { data?: unknown }).data;
  if (data && typeof data === 'object' && 'requestId' in data && typeof data.requestId === 'string') {
    return data.requestId;
  }
  try {
    const parsed = JSON.parse(err.message) as { requestId?: unknown };
    if (typeof parsed.requestId === 'string') return parsed.requestId;
  } catch {
    // message is not JSON
  }
  return null;
}

export function fmtDateTime(iso: Date | string | null | undefined): string {
  if (!iso) return '—';
  const d = iso instanceof Date ? iso : new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString(currentLocale());
}

/**
 * "3 h ago" style relative time in the caller's language (`t` from `useTranslation()`, so the
 * component re-renders when the language changes); null when the input is missing/unparseable.
 */
export function relTime(iso: Date | string | null | undefined, t: TFunction<'common'>): string | null {
  if (!iso) return null;
  const at = (iso instanceof Date ? iso : new Date(iso)).getTime();
  if (Number.isNaN(at)) return null;
  const diff = Date.now() - at;
  const past = diff >= 0;
  const s = Math.round(Math.abs(diff) / 1000);
  if (s < 60) return past ? t('time.justNow') : t('time.inAMoment');
  const m = Math.round(s / 60);
  if (m < 60) return past ? t('time.minAgo', { count: m }) : t('time.minFromNow', { count: m });
  const h = Math.round(m / 60);
  if (h < 48) return past ? t('time.hAgo', { count: h }) : t('time.hFromNow', { count: h });
  const d = Math.round(h / 24);
  if (d < 60) return past ? t('time.dAgo', { count: d }) : t('time.dFromNow', { count: d });
  return new Date(at).toLocaleDateString(currentLocale());
}

/**
 * Machine code + details of the domain error behind a failed call — tRPC puts them in
 * `data.domainCode` / `data.details`, REST in the JSON body — so the UI can word known
 * failures in the user's language instead of echoing the server's English message.
 */
export function domainErrorOf(err: unknown): { code: string; details: Record<string, unknown> | null } | null {
  if (!(err instanceof Error)) return null;
  const data = (err as { data?: unknown }).data;
  if (data && typeof data === 'object' && 'domainCode' in data && typeof data.domainCode === 'string') {
    return { code: data.domainCode, details: detailsOf('details' in data ? data.details : null) };
  }
  try {
    const parsed = JSON.parse(err.message) as { code?: unknown; details?: unknown };
    if (typeof parsed.code === 'string') return { code: parsed.code, details: detailsOf(parsed.details) };
  } catch {
    // message is not JSON
  }
  return null;
}

function detailsOf(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
}

export function truncate(s: string, max: number): string {
  return s.length > max ? `${s.slice(0, max)}…` : s;
}

/** Compact one-line summary of a jsonb stats object for dense tables. */
export function statsSummary(stats: unknown, max = 90): string {
  if (stats === null || stats === undefined) return '—';
  const s = JSON.stringify(stats);
  if (!s || s === '{}' || s === 'null') return '—';
  return truncate(s.replaceAll('"', ''), max);
}

/** Progress counters as the API stores them in untyped run stats (`{ processed, of }`). */
export interface Progress {
  processed?: number;
  of?: number;
}

/** Narrow an untyped `stats.<something>` value to a Progress object, or null when absent/malformed. */
export function progressOf(v: unknown): Progress | null {
  const o = v as Record<string, unknown> | null | undefined;
  if (!o || typeof o !== 'object') return null;
  return {
    processed: typeof o.processed === 'number' ? o.processed : undefined,
    of: typeof o.of === 'number' ? o.of : undefined,
  };
}

/** Display any untyped value as text; objects become JSON, null/undefined become ''. */
export function display(v: unknown): string {
  switch (typeof v) {
    case 'string':
      return v;
    case 'number':
    case 'boolean':
    case 'bigint':
      return String(v);
    case 'object':
      return v === null ? '' : JSON.stringify(v);
    default:
      return '';
  }
}

export function prettyJson(v: unknown): string {
  try {
    return JSON.stringify(v, null, 2) ?? String(v);
  } catch {
    return String(v);
  }
}
